import { useEffect, useRef, useCallback, useState } from 'react';
import { useGeneratorStore } from '../generatorStore';
import { api } from '../../lib/api/client';
import { useSSE } from '../hooks/useSSE';

/**
 * GeneratingStep（Phase 4 · Ticket P4-01 SSE + P4-02 重试）
 * - 首次进入自动 POST /api/ai/generate 启动生成
 * - 优先 SSE：EventSource 连接 /api/ai/stream，实时接收进度
 * - 降级轮询：SSE 不可用时 fallback 到 exponential backoff 轮询
 * - 失败处理：连续失败 3 次后显示重试按钮；单页失败显示红色标记
 * - 取消生成：前端停止 SSE + 标记 cancelled
 * - 完成后自动跳转到 done
 */

const MIN_POLL_INTERVAL = 3000;  // 3s
const MAX_POLL_INTERVAL = 30000; // 30s
const BACKOFF_FACTOR = 1.5;
const MAX_RETRIES = 3;

function getStepMessage(res: any): string {
  const pct = res.percentage ?? 0;
  const currentPage = res.current_page ?? res.currentPage ?? 0;
  const totalPages = res.total_pages ?? res.totalPages ?? 1;
  if (pct < 10) return '📖 正在规划故事大纲...';
  if (pct < 30) return `🎨 正在绘制第 ${currentPage}/${totalPages} 页插画...`;
  if (pct < 50) return '🌈 风格锚定：统一全书画风...';
  if (pct < 70) return `🏗️ 正在生成 3D 场景（第 ${currentPage}/${totalPages} 页）...`;
  if (pct < 90) return '🎙 正在为故事配音...';
  if (pct < 100) return '✨ 最终合成中...';
  return '🎉 绘本创作完成！';
}

export default function GeneratingStep() {
  const {
    params,
    status,
    setStatus,
    setStep,
    setError,
    setSubmitting,
    setPollRetries,
    setCancelled,
    isSubmitting,
    pollRetries,
    cancelled,
  } = useGeneratorStore();

  const [localMessage, setLocalMessage] = useState('准备中...');
  const [useFallback, setUseFallback] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);
  const pollIntervalRef = useRef(MIN_POLL_INTERVAL);

  // ── 状态更新辅助 ──
  const applyStatus = useCallback(
    (res: any) => {
      if (!mountedRef.current) return;
      const nextStatus = {
        sessionId: status?.sessionId || res.session_id || '',
        status: (res.status as 'generating' | 'completed' | 'failed') || 'generating',
        currentPage: res.current_page ?? res.currentPage ?? status?.currentPage ?? 0,
        totalPages: res.total_pages ?? res.totalPages ?? status?.totalPages ?? params.pages,
        percentage: res.percentage ?? status?.percentage ?? 0,
        message: getStepMessage(res),
        pages: res.pages ?? status?.pages ?? [],
        error: res.error,
      };
      setStatus(nextStatus);
      setLocalMessage(getStepMessage(res));
      setPollRetries(0);
      pollIntervalRef.current = MIN_POLL_INTERVAL;

      if (nextStatus.status === 'completed') {
        setSubmitting(false);
        setStep('done');
      } else if (nextStatus.status === 'failed') {
        setSubmitting(false);
        setError(res.error || '生成失败');
      }
    },
    [status, params.pages, setStatus, setStep, setError, setSubmitting, setPollRetries]
  );

  // ── 启动生成 ──
  const startGeneration = useCallback(async () => {
    if (isSubmitting) return;
    setSubmitting(true);
    setPollRetries(0);
    setCancelled(false);
    setUseFallback(false);
    pollIntervalRef.current = MIN_POLL_INTERVAL;
    try {
      const result = await api.ai.generate({
        theme: params.theme,
        pages: params.pages,
        ageGroup: params.ageGroup,
        language: params.language,
        style: params.style,
        tier: params.tier,
        styleReference: params.styleReference,
      });
      if (!mountedRef.current) return;
      if (result && result.session_id) {
        setStatus({
          sessionId: result.session_id,
          status: 'generating',
          currentPage: 0,
          totalPages: params.pages,
          percentage: 0,
          message: '开始生成...',
          pages: [],
        });
      } else {
        throw new Error('生成启动失败：后端未返回 session_id');
      }
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err instanceof Error ? err.message : '生成请求失败');
      setSubmitting(false);
    }
  }, [params, isSubmitting, setSubmitting, setStatus, setError, setPollRetries, setCancelled]);

  // ── 清理轮询 ──
  const clearPoll = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // ── SSE：实时推送 ──
  useSSE({
    sessionId: status?.sessionId ?? null,
    onMessage: applyStatus,
    onError: (err) => {
      console.warn('SSE error, falling back to polling:', err);
      setUseFallback(true);
    },
    onComplete: () => {
      setUseFallback(false);
      clearPoll();
    },
    onFallback: () => {
      setUseFallback(true);
    },
  });

  // ── Fallback 轮询（SSE 不可用时） ──
  useEffect(() => {
    if (!useFallback || !status || status.status !== 'generating' || cancelled) return;
    clearPoll();

    const doPoll = async () => {
      try {
        const res = await api.ai.status(status.sessionId);
        if (!mountedRef.current) return;
        applyStatus(res);
        if (res.status === 'completed' || res.status === 'failed') {
          clearPoll();
        }
      } catch (err) {
        if (!mountedRef.current) return;
        const nextRetries = pollRetries + 1;
        setPollRetries(nextRetries);
        if (nextRetries >= MAX_RETRIES) {
          clearPoll();
          setError(err instanceof Error ? err.message : '轮询连续失败，请检查网络后重试');
          setSubmitting(false);
          return;
        }
        pollIntervalRef.current = Math.min(
          MAX_POLL_INTERVAL,
          Math.round(pollIntervalRef.current * BACKOFF_FACTOR)
        );
        clearPoll();
        intervalRef.current = setInterval(doPoll, pollIntervalRef.current);
      }
    };

    intervalRef.current = setInterval(doPoll, pollIntervalRef.current);
    return clearPoll;
  }, [useFallback, status, cancelled, pollRetries, setPollRetries, setError, setSubmitting, applyStatus, clearPoll]);

  // ── 首次进入自动启动 ──
  useEffect(() => {
    mountedRef.current = true;
    if (!status || cancelled) {
      startGeneration();
    }
    return () => {
      mountedRef.current = false;
      clearPoll();
    };
  }, [status, cancelled, startGeneration, clearPoll]);

  // ── 取消生成 ──
  const handleCancel = useCallback(() => {
    clearPoll();
    setCancelled(true);
    setStatus(null);
    setSubmitting(false);
    setPollRetries(0);
    setError(null);
    setStep('style');
    setUseFallback(false);
  }, [clearPoll, setCancelled, setStatus, setSubmitting, setPollRetries, setError, setStep]);

  // ── P4-02: 单页失败自动重试 ──
  const retryingPagesRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    if (status?.status !== 'completed' || !status.sessionId) return;

    const failedPages = (status.pages ?? []).filter((p: any) => !p.image_path);
    if (failedPages.length === 0) return;

    const retryOne = async (page: any) => {
      const pn = page.page_number;
      if (retryingPagesRef.current.has(pn)) return;
      retryingPagesRef.current.add(pn);

      try {
        await api.ai.continue({
          session_id: status.sessionId,
          current_page_id: String(pn),
        });
        // 成功后更新状态（轮询/SSE 会自动刷新）
      } catch (err) {
        console.warn(`Page ${pn} retry failed:`, err);
      } finally {
        retryingPagesRef.current.delete(pn);
      }
    };

    // 串行重试所有失败页面，间隔 5s
    failedPages.forEach((page, idx) => {
      setTimeout(() => retryOne(page), idx * 5000);
    });
  }, [status?.status, status?.sessionId]);

  // ── 手动重试 ──
  const handleRetry = useCallback(() => {
    setError(null);
    setPollRetries(0);
    setCancelled(false);
    setUseFallback(false);
    if (status?.sessionId) {
      pollIntervalRef.current = MIN_POLL_INTERVAL;
      setStatus({ ...status, status: 'generating' });
    } else {
      startGeneration();
    }
  }, [status, setError, setPollRetries, setCancelled, setStatus, startGeneration]);

  // ── 渲染 ──
  const pct = status?.percentage ?? 0;
  const message = localMessage || status?.message || '准备中...';
  const pages = status?.pages ?? [];
  const isFailed = status?.status === 'failed' || pollRetries >= MAX_RETRIES;
  const isCompleted = status?.status === 'completed';

  return (
    <div style={containerStyle}>
      <h2 style={titleStyle}>正在创作绘本...</h2>

      {/* 连接状态指示 */}
      <div style={connectionStyle}>
        {useFallback ? (
          <span style={{ color: '#fbbf24' }}>⚠ SSE 不可用，已降级到轮询</span>
        ) : status?.status === 'generating' ? (
          <span style={{ color: '#4ade80' }}>● 实时推送中</span>
        ) : null}
      </div>

      {/* 进度条 */}
      <div style={progressWrapStyle}>
        <div style={progressBarBgStyle}>
          <div style={{ ...progressBarFillStyle, width: `${pct}%` }} />
        </div>
        <div style={progressTextStyle}>{pct}%</div>
      </div>

      {/* 步骤文字 */}
      <div style={stepTextStyle}>
        {isFailed ? (
          <span style={{ color: '#ff8a8a' }}>
            ❌ {pollRetries >= MAX_RETRIES ? '轮询连续失败，请检查网络后重试' : status?.error || '生成失败'}
          </span>
        ) : (
          message
        )}
      </div>

      {/* 连续失败警告 */}
      {pollRetries > 0 && pollRetries < MAX_RETRIES && (
        <div style={warningStyle}>
          ⚠️ 轮询失败 {pollRetries}/{MAX_RETRIES} 次，正在重试（间隔 {Math.round(pollIntervalRef.current / 1000)}s）...
        </div>
      )}

      {/* 已生成页面缩略图 */}
      {pages.length > 0 && (
        <div style={pagesWrapStyle}>
          <div style={pagesLabelStyle}>已生成页面</div>
          <div style={pagesGridStyle}>
            {pages.map((p) => {
              const failed = !p.image_path;
              return (
                <div
                  key={p.page_number}
                  style={{
                    ...pageThumbStyle,
                    border: failed ? '1.5px solid rgba(255,100,100,0.5)' : '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  {p.image_path ? (
                    <img src={p.image_path} alt={p.title} style={thumbImgStyle} />
                  ) : (
                    <div style={thumbPlaceholderStyle}>{failed ? '❌' : '\u{1F4F7}'}</div>
                  )}
                  <div style={thumbTitleStyle}>
                    {p.title}
                    {failed && <span style={{ color: '#ff8a8a', marginLeft: '4px' }}>(失败)</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 预计时间提示 */}
      {!isFailed && !isCompleted && status?.totalPages && (
        <div style={etaStyle}>
          预计剩余时间：约 {Math.max(1, Math.round((status.totalPages - (status.currentPage || 0)) * 8))} 分钟
          {pollRetries > 0 && ` · 轮询间隔 ${Math.round(pollIntervalRef.current / 1000)}s`}
        </div>
      )}

      {/* 操作按钮 */}
      <div style={actionsStyle}>
        {isFailed ? (
          <button onClick={handleRetry} style={btnRetryStyle}>
            🔄 重试
          </button>
        ) : (
          <button onClick={handleCancel} style={btnCancelStyle}>
            {cancelled ? '已取消' : '取消生成'}
          </button>
        )}
      </div>
    </div>
  );
}

const containerStyle: React.CSSProperties = {
  maxWidth: '720px',
  margin: '0 auto',
  padding: '40px 20px',
};

const titleStyle: React.CSSProperties = {
  fontSize: '24px',
  fontWeight: 'bold',
  color: '#fff',
  marginBottom: '12px',
  textAlign: 'center',
};

const connectionStyle: React.CSSProperties = {
  fontSize: '12px',
  textAlign: 'center',
  marginBottom: '16px',
  minHeight: '18px',
};

const progressWrapStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '16px',
};

const progressBarBgStyle: React.CSSProperties = {
  flex: 1,
  height: '12px',
  borderRadius: '6px',
  background: 'rgba(255,255,255,0.1)',
  overflow: 'hidden',
};

const progressBarFillStyle: React.CSSProperties = {
  height: '100%',
  borderRadius: '6px',
  background: 'linear-gradient(90deg, #7c4dff, #448aff)',
  transition: 'width 0.6s ease',
};

const progressTextStyle: React.CSSProperties = {
  fontSize: '15px',
  fontWeight: 600,
  color: '#fff',
  minWidth: '48px',
  textAlign: 'right',
};

const stepTextStyle: React.CSSProperties = {
  fontSize: '14px',
  color: 'rgba(255,255,255,0.7)',
  textAlign: 'center',
  marginBottom: '20px',
  minHeight: '20px',
};

const warningStyle: React.CSSProperties = {
  fontSize: '13px',
  color: '#fbbf24',
  textAlign: 'center',
  marginBottom: '20px',
};

const pagesWrapStyle: React.CSSProperties = {
  marginBottom: '24px',
};

const pagesLabelStyle: React.CSSProperties = {
  fontSize: '13px',
  color: 'rgba(255,255,255,0.5)',
  marginBottom: '12px',
};

const pagesGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
  gap: '12px',
};

const pageThumbStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.05)',
  borderRadius: '10px',
  padding: '8px',
  textAlign: 'center',
  transition: 'border-color 0.2s ease',
};

const thumbImgStyle: React.CSSProperties = {
  width: '100%',
  height: '80px',
  objectFit: 'cover',
  borderRadius: '6px',
  marginBottom: '6px',
};

const thumbPlaceholderStyle: React.CSSProperties = {
  width: '100%',
  height: '80px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '28px',
  borderRadius: '6px',
  marginBottom: '6px',
  background: 'rgba(255,100,100,0.08)',
};

const thumbTitleStyle: React.CSSProperties = {
  fontSize: '11px',
  color: 'rgba(255,255,255,0.6)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const etaStyle: React.CSSProperties = {
  fontSize: '12px',
  color: 'rgba(255,255,255,0.35)',
  textAlign: 'center',
  marginBottom: '20px',
};

const actionsStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  marginTop: '20px',
};

const btnCancelStyle: React.CSSProperties = {
  padding: '10px 24px',
  borderRadius: '10px',
  border: '1px solid rgba(255,100,100,0.4)',
  background: 'rgba(255,100,100,0.1)',
  color: '#ff8a8a',
  fontSize: '14px',
  cursor: 'pointer',
  transition: 'all 0.15s ease',
};

const btnRetryStyle: React.CSSProperties = {
  padding: '12px 32px',
  borderRadius: '10px',
  border: 'none',
  background: 'linear-gradient(135deg, #7c4dff, #448aff)',
  color: '#fff',
  fontSize: '15px',
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'all 0.15s ease',
};
